import axios from 'axios'
import ApiClient from 'api-client/ApiClient'
import {
  appUrlRoot,
  GlobalPreferences,
  imageUrlRoot,
  opencloudProductName,
  pcdProductName,
} from 'app/constants'
import store from 'app/store'
import config from 'app-config'
import { clientActions } from 'core/client/clientReducers'
import { preferencesActions } from 'core/session/preferencesReducers'
import { themeActions } from 'core/session/themeReducers'
import serenityLightTheme from 'core/themes/serenity/serenityLight'
import { getFeaturesJson, isDevelopmentMode } from 'core/utils/helpers'
import Watchdog from 'core/watchdog'
import { pathStrOr } from 'utils/fp'
import { setupFromConfig } from 'utils/registry'

const { dispatch } = store

setupFromConfig(config)

if (config.apiHost === undefined) {
  throw new Error('config.js does not contain "apiHost"')
}

// Proxy mode: override apiHost and install a global axios interceptor so
// ALL requests (both ApiClient and standalone axios.get) go through the proxy.
if (isDevelopmentMode() && process.env.VITE_DU_TARGET) {
  config.apiHost = process.env.VITE_DU_TARGET
  try {
    const duOrigin = new URL(process.env.VITE_DU_TARGET).origin
    axios.interceptors.request.use((reqConfig) => {
      if (reqConfig.url?.startsWith(duOrigin)) {
        reqConfig.url = reqConfig.url.slice(duOrigin.length) || '/'
      }
      return reqConfig
    })
  } catch {
    // invalid VITE_DU_TARGET — skip interceptor
  }
}

// Make sure the app starts in its default state
dispatch(
  clientActions.updateFrameValues({
    sidebarState: 'expanded',
    sidebarPane: 'default',
  }),
)

// Initialize ApiClient singleton
ApiClient.init({ keystoneEndpoint: `${config.apiHost}/keystone` })

// Start the watchdog heartbeat
Watchdog.init(1000, dispatch)
window.addEventListener('beforeunload', () => Watchdog.getInstance().destroy())

const loadTheme = async () => {
  const { preferenceStore } = ApiClient.getInstance()
  try {
    const response = await preferenceStore.getGlobalPreference(GlobalPreferences.Theme)
    const customTheme = JSON.parse(response.value)

    if (!customTheme) {
      dispatch(themeActions.updateTheme(serenityLightTheme))
      return
    }

    dispatch(
      preferencesActions.updateLogo({
        logoUrl: customTheme.logoUrl,
        logoSrc: customTheme.logoSrc,
        logoFileName: customTheme.logoFileName,
      }),
    )
    dispatch(
      preferencesActions.updateLoginLogo({
        loginLogoUrl: customTheme.loginLogoUrl,
        loginLogoSrc: customTheme.loginLogoSrc,
        loginLogoFileName: customTheme.loginLogoFileName,
      }),
    )

    // if (customTheme.headerColor || customTheme.sidenavColor) {
    //   dispatch(themeActions.updateThemeComponent(generateThemeUpdatePayload(customTheme)))
    // }
    dispatch(themeActions.updateGlobalTheme(customTheme))
  } catch (err) {
    // Reset the prefs if store not available
    dispatch(preferencesActions.resetGlobalPrefs())
  }
}

// Update document title and favicon
const updateAppBranding = (productType: string) => {
  document.title = productType === 'opencloud' ? opencloudProductName : pcdProductName

  const faviconUrl =
    productType === 'opencloud'
      ? `${imageUrlRoot}/opencloud/r-Icon-White-Square.png`
      : `${appUrlRoot}/favicon.ico`

  // Remove existing favicons
  document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove())

  const newFavicon = document.createElement('link')
  newFavicon.rel = 'icon'
  newFavicon.href = faviconUrl
  document.head.appendChild(newFavicon)
}

const initApp = async () => {
  const features = await getFeaturesJson()
  const productType = pathStrOr('pcd', 'experimental.product_type', features)

  // During app initialization, store the product type in localStorage to ensure that
  // routes have access to the correct product type even before the app starts.
  // This is necessary because route registration happens during module imports, which can
  // occur before the Redux store is initialized.
  localStorage.setItem('productType', productType)

  updateAppBranding(productType)

  if (productType !== 'opencloud') {
    loadTheme()
  }
}

initApp()
